import { useSuspenseQuery } from '@tanstack/react-query';
import { API } from 'api';
import { useSelector } from 'react-redux';

import { DOMAINS, IMPORTANCE, SECTIONS } from '@repo/monitoring';

import { apiBasePath, apiName } from 'assets';
import { getMainAccountId } from 'store/account';
import { Agreement } from 'types/Agreement';

const getAgreements = async (mainAccountId: string): Promise<Agreement[]> => {
  const response = await API.get(
    apiName,
    `${apiBasePath}/agreements`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.CONDITIONS,
        importance: IMPORTANCE.CRITICAL,
      },
      context: {
        mainAccountId,
      },
    },
  );
  return response?.result ?? [];
};

// keyed on the main account id, which resolves to the impersonated account, so
// a practitioner switching users never reads the previous user's agreements
export const getAgreementsQuery = (mainAccountId?: string | null) => ({
  queryKey: ['agreements', mainAccountId],
  queryFn: async () =>
    mainAccountId ? await getAgreements(mainAccountId) : [],
});

// suspends so a consumer cannot render against an unknown agreement state; the
// query cache is the only store, since the server owns the record
const useAgreements = () => {
  const mainAccountId = useSelector(getMainAccountId);

  return useSuspenseQuery<Agreement[]>(getAgreementsQuery(mainAccountId));
};

export default useAgreements;
